import { QrCode } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { SiteWithAcCredentialsSettingsResponse } from '../../apiRtk/be4feApi';
import { useUrlParams } from '../../helpers/urlParams';
import { useModal } from '../../hooks/useModal';
import { getApartmentDevices } from '../../modules/apartments/selectors';
import { VisitorModalContext, VisitorModalOpeningContext } from '../../modules/commonTypes';
import { IUser } from '../../modules/user/store';
import { isUserGuest } from '../../modules/user/utils';
import VisitorModal from '../../pages/apartmentAdmin/ApartmentAdminUsersOverviewPage/components/VisitorModal/VisitorModal';
import RevokeUserPinDialog from '../RevokeUserPinModal';
import CredentialAlert from './CredentialAlert';
import DormakabaAlert from './DormakabaAlert';
import { useGetGlobalHooks } from './hooks/getGlobalHooks';
import useDormakabaAccess from './hooks/useDormakabaAccess';
import messages from './messages';
import PanelQr from './PanelQr';
import SectionTitle from './SectionTitle';

interface ISectionQr {
  user: IUser;
  userPinData?: {
    active: boolean;
    id: string;
    pin: string;
  };
  siteData: SiteWithAcCredentialsSettingsResponse;
}

function SectionQr({ siteData, user, userPinData }: ISectionQr) {
  const { formatMessage, isCompanyOrSiteAdmin } = useGetGlobalHooks();
  const isPinEmpty = !userPinData?.pin;
  const { companyId, siteId, userId } = useUrlParams();
  const apartmentDevices = useSelector(getApartmentDevices).data || [];
  const isGuestQrEnabled = isUserGuest(user.role) && siteData?.services.ACCESS_CONTROL?.guestShowQr;
  const isSiteWithDormakabaIntegrationEnabled = siteData?.integrations?.DORMAKABA?.enabled;
  const isUserPinLength6 = siteData?.services?.ACCESS_CONTROL?.userPinLength === 6;
  const isGuestPinLength6 = siteData?.services?.ACCESS_CONTROL?.guestPinLength === 6;
  const { Modal: RevokeUserPinModal, onOpen: onOpenRevokeUserPinModal } = useModal({
    Modal: RevokeUserPinDialog,
  });
  const { Modal: VisitorUModal, onOpen: onOpenVisitorUModal } = useModal({
    Modal: VisitorModal,
  });

  const [displayInfoBox, setDisplayInfoBox] = useState(isPinEmpty);

  const toggleDisplayInfoBox = () => {
    setDisplayInfoBox((prevState) => !prevState);
  };

  const { enrichedUser, hasDormakabaAccessForUser } = useDormakabaAccess({
    companyId: companyId!,
    siteId: siteId!,
    user,
    userId: userId!,
  });

  return (
    <>
      <SectionTitle
        isEmpty={isPinEmpty}
        onInfoClick={toggleDisplayInfoBox}
        startIcon={<QrCode color={isPinEmpty ? 'secondary' : 'primary'} />}
        title={formatMessage(messages.userAccessFormQrTitle)}
      />
      <DormakabaAlert
        companyId={companyId!.toString()}
        hasDormakabaAccessForUser={hasDormakabaAccessForUser}
        isGuestPinLength6={isGuestPinLength6}
        isUserPinLength6={isUserPinLength6}
        isVisitor={isUserGuest(user.role)}
        isSiteWithDormakabaIntegrationEnabled={isSiteWithDormakabaIntegrationEnabled}
        pinLength={userPinData?.pin ? Number(userPinData.pin.length) : undefined}
        siteId={siteId!.toString()}
      />
      {isPinEmpty ? (
        <Button color="primary" onClick={onOpenVisitorUModal} startIcon={<AddIcon />} sx={{ mb: 2 }} variant="outlined">
          <FormattedMessage {...messages.userAccessFormQrGenerateNewQr} />
        </Button>
      ) : (
        <PanelQr pin={userPinData?.pin} />
      )}
      {displayInfoBox && (
        <CredentialAlert
          infoText={formatMessage(messages.userAccessFormQrInfoDescription, {
            br: <br />,
          })}
        />
      )}
      {!isPinEmpty && (
        <Grid sx={{ mt: 4 }}>
          <Button color="error" disabled={!isCompanyOrSiteAdmin} onClick={onOpenRevokeUserPinModal}>
            <FormattedMessage {...messages.revokeUserQrButtonDeleteQr} />
          </Button>
        </Grid>
      )}
      <RevokeUserPinModal user={user} isGuestQrEnabled={isGuestQrEnabled} />
      <VisitorUModal
        msgContext={VisitorModalContext.AA_MODAL_NEW_GUEST_PIN}
        apartmentInfo={{ companyId: companyId!, siteId: siteId! }}
        apartmentDevices={user?.apartments.length ? apartmentDevices : []}
        siteName={siteData.name}
        user={enrichedUser}
        isRegenerate
        openingContext={VisitorModalOpeningContext.SECTION_QR}
      />
    </>
  );
}

export default SectionQr;
